import { z } from 'zod';
import { estimatingBasisPointsSchema, estimatingCurrencyCodeSchema, estimatingEstimateStatusSchema, estimatingMoneyCentsSchema, estimatingQuantityTenThousandthsSchema, } from './estimating-estimate.contract.js';
export const estimatingFinanceBillableRevisionsCapability = 'estimating.billable-revisions.v1';
export const estimatingFinanceBillableRevisionSlot = 'finance.invoice-billable-revision';
export const estimatingFinanceBillableRevisionProviderId = 'estimating.server.finance-billable-revision';
const nullableText = (maximum) => z.string().trim().min(1).max(maximum).nullable();
export const estimatingFinanceBillableRevisionRequestSchema = z.strictObject({
    consumerModuleId: z.literal('finance'),
    requestedCapability: z.literal(estimatingFinanceBillableRevisionsCapability),
    resourceType: z.literal('finance.invoice'),
    resourceId: z.string().uuid(),
    preferredSourceId: z.string().uuid().nullable().default(null),
    projectId: z.string().uuid(),
});
export const estimatingFinanceBillableLineSchema = z.strictObject({
    lineKey: z.string().uuid(),
    lineNumber: z.number().int().positive(),
    sourceCapability: nullableText(160),
    sourceId: nullableText(240),
    sourceLabel: nullableText(240),
    groupLabel: nullableText(120),
    name: z.string().trim().min(1).max(240),
    description: nullableText(4_000),
    quantityTenThousandths: estimatingQuantityTenThousandthsSchema,
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: estimatingMoneyCentsSchema,
    taxable: z.boolean(),
    lineTotalCents: estimatingMoneyCentsSchema,
});
export const estimatingFinanceBillableRevisionFactSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal(estimatingFinanceBillableRevisionsCapability),
    estimateId: z.string().uuid(),
    estimateVersionId: z.string().uuid(),
    estimateRevision: z.number().int().positive(),
    estimateStatus: estimatingEstimateStatusSchema,
    estimateContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    projectId: z.string().uuid(),
    projectRevision: z.number().int().positive(),
    title: z.string().trim().min(1).max(240),
    currencyCode: estimatingCurrencyCodeSchema,
    taxBasisPoints: estimatingBasisPointsSchema,
    totals: z.strictObject({
        subtotalCents: estimatingMoneyCentsSchema,
        discountCents: estimatingMoneyCentsSchema,
        taxCents: estimatingMoneyCentsSchema,
        totalCents: estimatingMoneyCentsSchema,
    }),
    lines: z.array(estimatingFinanceBillableLineSchema).min(1).max(500),
}).superRefine((fact, context) => {
    const subtotal = fact.lines.reduce((sum, line) => sum + BigInt(line.lineTotalCents), 0n);
    if (subtotal !== BigInt(fact.totals.subtotalCents)) {
        context.addIssue({
            code: 'custom',
            path: ['totals', 'subtotalCents'],
            message: 'Estimating billable lines must reconcile to the retained subtotal.',
        });
    }
    const expectedTotal = fact.totals.subtotalCents - fact.totals.discountCents + fact.totals.taxCents;
    if (fact.totals.totalCents !== expectedTotal) {
        context.addIssue({
            code: 'custom',
            path: ['totals', 'totalCents'],
            message: 'Estimating billable totals must reconcile exactly.',
        });
    }
});
export const estimatingFinanceBillableRevisionResponseSchema = z.discriminatedUnion('state', [
    estimatingFinanceBillableRevisionFactSchema,
    z.strictObject({
        state: z.literal('unavailable'),
        capability: z.literal(estimatingFinanceBillableRevisionsCapability),
        reasonCode: z.enum([
            'no-current-estimate',
            'preferred-estimate-not-found',
            'estimate-not-billable',
            'no-billable-lines',
        ]),
    }),
]);
//# sourceMappingURL=estimating-finance-billable-revision.contract.js.map